import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { getBackendHost } from '@/helpers/backend';
import { apiRoutes } from '@/utils/routes';

const retryCounts = new Map<string, number>();
const MAX_RETRIES = 3;

export const onFulfilledResponse = (response: AxiosResponse<any, any>): any => {
  const requestKey = `${response.config.method || 'GET'}:${response.config.url}`;
  retryCounts.set(requestKey, 0);
  return response;
};

export const onFailedResponse = async (error: any): Promise<any> => {
  const originalRequest = error.config as AxiosRequestConfig;

  const requestKey = `${originalRequest.method || 'GET'}:${originalRequest.url}`;

  const currentRetryCount = retryCounts.get(requestKey) || 0;

  if (error.response?.status === 401 || error.response?.status === 403) {
    if (currentRetryCount < MAX_RETRIES) {
      retryCounts.set(requestKey, currentRetryCount + 1);

      try {
        await axiosInstance.get(apiRoutes.refreshToken);

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        throw refreshError;
      }
    } else {
      console.error('Max retry attempts reached');
    }
  }

  return Promise.reject(error);
};

// this makes array[] becomes array=1,2,3
export const cleanParams = (config: InternalAxiosRequestConfig<any>): InternalAxiosRequestConfig<any> => {
  if (config.params) {
    Object.keys(config.params).forEach((key) => {
      if (Array.isArray(config.params[key])) {
        if (config.params[key].length === 1) {
          config.params[key] = config.params[key][0];
        } else {
          config.params[key] = config.params[key].join(',');
        }
      }
    });
  }
  return config;
};

export const axiosInstance = axios.create({
  baseURL: getBackendHost(),
  withCredentials: true,
});

axiosInstance.interceptors.response.use(onFulfilledResponse, onFailedResponse);
axiosInstance.interceptors.request.use(cleanParams);

export default {
  async get<RequestEntity>(url: string, config: AxiosRequestConfig = {}) {
    return axiosInstance.get<RequestEntity>(url, config);
  },
  post<RequestEntity>(url: string, body: Record<string, any> | FormData, config: AxiosRequestConfig = {}) {
    return axiosInstance.post<RequestEntity>(url, body, config);
  },
  put<RequestEntity>(url: string, body: Record<string, any>, config: AxiosRequestConfig = {}) {
    return axiosInstance.put<RequestEntity>(url, body, config);
  },
  patch<RequestEntity>(url: string, body: Record<string, any>, config: AxiosRequestConfig = {}) {
    return axiosInstance.patch<RequestEntity>(url, body, config);
  },
  delete<RequestEntity>(url: string, config: AxiosRequestConfig = {}) {
    return axiosInstance.delete<RequestEntity>(url, config);
  },
};
